// Two-Factor Authentication Utilities - Firebase DB Based with OTPAuth
import * as OTPAuth from "otpauth";
import QRCode from "qrcode";
import { logAdminActivity } from "./adminSecurity";
import {
  getAdminUserData,
  enable2FAForUser,
  disable2FAForUser,
  markBackupCodeAsUsed,
} from "../firebase";

// Generate 2FA secret for new setup
export const generate2FASecret = (userEmail) => {
  // Create a new TOTP object
  const totp = new OTPAuth.TOTP({
    issuer: "Almanya Danışmanlık",
    label: userEmail,
    algorithm: "SHA1",
    digits: 6,
    period: 30,
  });

  return {
    secret: totp.secret.base32,
    otpauth_url: totp.toString(),
    manual_entry_key: totp.secret.base32,
    totp: totp, // Keep the TOTP object for later use
  };
};

// Generate QR code for 2FA setup
export const generateQRCode = async (otpauth_url) => {
  try {
    const qrCodeDataURL = await QRCode.toDataURL(otpauth_url, {
      width: 256,
      margin: 2,
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    });
    return qrCodeDataURL;
  } catch (error) {
    console.error("QR Code generation error:", error);
    throw error;
  }
};

// Verify 2FA token
export const verify2FAToken = (token, secret) => {
  try {
    // Create TOTP object from secret
    const totp = new OTPAuth.TOTP({
      issuer: "Almanya Danışmanlık",
      label: "Admin",
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: OTPAuth.Secret.fromBase32(secret),
    });

    // Verify token with some time window tolerance
    const delta = totp.validate({
      token: token,
      window: 2, // Allow 2 time steps (60 seconds) tolerance
    });

    const verified = delta !== null;

    if (verified) {
      logAdminActivity("2fa_verification_success", {
        timestamp: new Date().toISOString(),
      });
    } else {
      logAdminActivity("2fa_verification_failed", {
        timestamp: new Date().toISOString(),
        token_provided: token.replace(/./g, "*"), // Hide actual token in logs
      });
    }

    return verified;
  } catch (error) {
    console.error("2FA verification error:", error);
    logAdminActivity("2fa_verification_error", {
      timestamp: new Date().toISOString(),
      error: error.message,
    });
    return false;
  }
};

// Save 2FA setup to Firebase
export const save2FASecret = async (secret, userEmail, uid, backupCodes) => {
  try {
    const secretData = {
      secret,
      userEmail,
      setupDate: new Date().toISOString(),
    };

    await enable2FAForUser(uid, secretData, backupCodes);

    logAdminActivity("2fa_setup_completed", {
      userEmail,
      uid,
      timestamp: new Date().toISOString(),
    });

    return true;
  } catch (error) {
    console.error("2FA secret save error:", error);
    return false;
  }
};

// Get 2FA secret from Firebase
export const get2FASecret = async (uid) => {
  try {
    const userData = await getAdminUserData(uid);
    return userData?.twoFactorSecret?.secret || null;
  } catch (error) {
    console.error("2FA secret retrieval error:", error);
    return null;
  }
};

// Check if 2FA is enabled for specific user (from Firebase)
export const is2FAEnabled = async (uid) => {
  try {
    if (!uid) return false;
    const userData = await getAdminUserData(uid);
    return userData?.twoFactorEnabled === true;
  } catch (error) {
    console.error("2FA enabled check error:", error);
    return false;
  }
};

// Generate backup codes (for account recovery)
export const generateBackupCodes = (count = 8) => {
  const codes = [];
  for (let i = 0; i < count; i++) {
    // Generate 8-character backup code
    const code =
      Math.random().toString(36).substr(2, 4).toUpperCase() +
      Math.random().toString(36).substr(2, 4).toUpperCase();
    codes.push(code);
  }

  // Codes will be saved to Firebase when 2FA is enabled
  return codes;
};

// Verify backup code (from Firebase)
export const verifyBackupCode = async (code, uid, userEmail) => {
  try {
    const userData = await getAdminUserData(uid);
    if (!userData?.backupCodes) return false;

    const upperCode = code.toUpperCase();
    const backupCodes = userData.backupCodes;

    // Check if code exists
    if (backupCodes.includes(upperCode)) {
      // Use the backup code (removes it from the list)
      const remainingCount = await markBackupCodeAsUsed(uid, upperCode);

      logAdminActivity("backup_code_used", {
        userEmail,
        uid,
        timestamp: new Date().toISOString(),
        remaining_codes: remainingCount,
      });

      return true;
    }

    logAdminActivity("backup_code_failed", {
      userEmail,
      uid,
      timestamp: new Date().toISOString(),
      code_provided: code.replace(/./g, "*"),
    });

    return false;
  } catch (error) {
    console.error("Backup code verification error:", error);
    return false;
  }
};

// Get remaining backup codes count (from Firebase)
export const getRemainingBackupCodes = async (uid) => {
  try {
    const userData = await getAdminUserData(uid);
    return userData?.backupCodes?.length || 0;
  } catch (error) {
    return 0;
  }
};

// Disable 2FA (with backup code verification)
export const disable2FA = async (backupCode, uid, userEmail) => {
  const isValidBackupCode = await verifyBackupCode(backupCode, uid, userEmail);
  if (!isValidBackupCode) {
    return false;
  }

  await disable2FAForUser(uid);

  logAdminActivity("2fa_disabled", {
    userEmail,
    uid,
    timestamp: new Date().toISOString(),
  });

  return true;
};

// Get 2FA setup info (from Firebase)
export const get2FAInfo = async (uid) => {
  try {
    const userData = await getAdminUserData(uid);
    if (!userData) return null;

    return {
      userEmail: userData.email,
      setupDate: userData.twoFactorSecret?.setupDate,
      isEnabled: userData.twoFactorEnabled,
      remainingBackupCodes: userData.backupCodes?.length || 0,
      twoFactorEnabledAt: userData.twoFactorEnabledAt,
    };
  } catch (error) {
    return null;
  }
};

// Clear all 2FA data (for logout/reset)
export const clear2FAData = () => {
  // 2FA data is now in Firebase, nothing to clear locally
  logAdminActivity("2fa_session_cleared", {
    timestamp: new Date().toISOString(),
  });
};

// Generate current TOTP token (for testing purposes only)
export const generateCurrentToken = (secret) => {
  if (process.env.NODE_ENV !== "development") {
    console.warn("generateCurrentToken should not be used in production");
    return null;
  }

  try {
    const totp = new OTPAuth.TOTP({
      issuer: "Almanya Danışmanlık",
      label: "Admin",
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: OTPAuth.Secret.fromBase32(secret),
    });

    return totp.generate();
  } catch (error) {
    console.error("Token generation error:", error);
    return null;
  }
};

// Validate 2FA setup completion
export const validate2FASetup = (token, secret) => {
  const isValid = verify2FAToken(token, secret);

  if (isValid) {
    logAdminActivity("2fa_setup_validated", {
      timestamp: new Date().toISOString(),
    });
  } else {
    logAdminActivity("2fa_setup_validation_failed", {
      timestamp: new Date().toISOString(),
    });
  }

  return isValid;
};

const twoFactorAuth = {
  generate2FASecret,
  generateQRCode,
  verify2FAToken,
  save2FASecret,
  get2FASecret,
  is2FAEnabled,
  generateBackupCodes,
  verifyBackupCode,
  getRemainingBackupCodes,
  disable2FA,
  get2FAInfo,
  clear2FAData,
  generateCurrentToken,
  validate2FASetup,
};

export default twoFactorAuth;
